import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Loading from '../../components/Loading';
import API from '../../services/api';
import { QUERY_KEYS } from '../../config/queryKey';
import Button from '../../components/Button';
import RoleForm from './RoleForm';
import { getPermissionName } from '../../config/permissions';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../components/Dialog';
export default function Roles() {
  const { isPending, error, data } = useQuery({
    queryKey: [QUERY_KEYS.GET_ROLES],
    queryFn: () => API.role.getRoles(),
  });

  const [selectedRole, setSelectedRole] = useState(null);

  return (
    <div className="container mx-auto bg-white">
      <div className="flex justify-between items-center mt-4 mb-8">
        <h1 className="text-2xl font-bold">Manage Roles</h1>
        <div className="flex flex-row gap-4">
          <Button
            onClick={() => {
              setSelectedRole({
                roleKey: '',
                roleName: '',
                permissions: [],
              });
            }}
          >
            Create Role
          </Button>
        </div>
      </div>
      {isPending && (
        <div className="w-full h-full flex justify-center items-center">
          <div className="w-10 h-10">
            <Loading />
          </div>
        </div>
      )}
      {error && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600">Error: {error.message}</p>
        </div>
      )}
      {!data && !isPending && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600">No data found for this range.</p>
        </div>
      )}
      {data && !isPending && (
        <div className="h-[calc(100vh-200px)] mt-2 overflow-auto border-t border-gray-200">
          <table className="w-full border-none">
            <thead className="sticky top-0 bg-white shadow-sm">
              <tr className="bg-gray-200">
                <th
                  scope="col"
                  className="sticky top-0 py-3 pl-4 pr-3 text-left text-sm font-bold uppercase tracking-wide text-gray-900"
                >
                  Role Key
                </th>
                <th
                  scope="col"
                  className="sticky top-0 px-2 py-3 text-left text-sm font-bold uppercase tracking-wide text-gray-900"
                >
                  Role Name
                </th>
                <th
                  scope="col"
                  className="sticky top-0 px-2 py-3 text-left text-sm font-bold uppercase tracking-wide text-gray-900"
                >
                  Permissions
                </th>
                <th
                  scope="col"
                  className="sticky top-0 px-2 py-3 text-left text-sm font-bold uppercase tracking-wide text-gray-900"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.map((role, roleIndex) => {
                return (
                  <RoleFormRow
                    key={roleIndex}
                    role={role}
                    setSelectedRole={setSelectedRole}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Dialog
        open={!!selectedRole}
        setOpen={setSelectedRole}
        title={selectedRole?.roleKey ? 'Edit Role' : 'Create Role'}
      >
        <RoleForm
          currentRole={selectedRole}
          actions={
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedRole(null);
              }}
            >
              Close
            </Button>
          }
          onCallback={() => {
            setSelectedRole(null);
          }}
        />
      </Dialog>
    </div>
  );
}

function RoleFormRow({ role, setSelectedRole }) {
  const queryClient = useQueryClient();

  const mutationDelete = useMutation({
    mutationFn: (role) => API.role.deleteRole(role.roleKey),
    onSuccess: () => {
      toast.success('Role deleted successfully');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_ROLES] });
    },
    onError: () => {
      toast.error('Failed to delete role');
    },
  });

  return (
    <tr>
      <td className="pl-2 py-2 whitespace-nowrap font-medium">
        {role.roleKey}
      </td>
      <td className="px-2 py-2 whitespace-nowrap">{role.roleName}</td>
      <td className="py-1 px-2 whitespace-nowrap">
        <div className="flex flex-col gap-2">
          {role.permissions.map((permission, permissionIndex) => {
            return (
              <div
                key={permissionIndex}
                className="text-sm w-fit text-gray-500 bg-gray-100 rounded-lg py-1 px-2"
              >
                {getPermissionName(permission)}
              </div>
            );
          })}
        </div>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        <div className="flex flex-row gap-2">
          <Button
            size="sm"
            onClick={() => {
              setSelectedRole(role);
            }}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={mutationDelete.isPending}
            onClick={() => {
              mutationDelete.mutate(role);
            }}
          >
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}
